import type { PageStore } from "@/store/PageStore";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

let pendingMessage: string | null = null;

interface SendPendingChatMessageArgs {
  pageStore: PageStore;
  message: string;
}

/**
 * Sends a pending user message that was set from the /chat/new route.
 * Waits for the session to be ready before sending to avoid race conditions.
 */
export const sendPendingChatMessage = async ({
  pageStore,
  message,
}: SendPendingChatMessageArgs): Promise<void> => {
  // react re-renders can fire this multiple times. skip if we're already pending
  if (pendingMessage) return;

  pendingMessage = message;
  const memItPage = pageStore.memItPage;

  // Set flag to indicate a new chat is pending (hide empty state)
  memItPage.setIsPendingNewChat(true);

  try {
    // Get or create a new session
    const sessionId = await memItPage.handleNewSession();

    // Wait for the session to be ready and available in the store
    // Poll until the session is available (with a timeout)
    const INTERVAL_MS = 100;
    const maxAttempts = 50; // 5 seconds max (50 * 100ms)
    let attempts = 0;

    do {
      // Check if the session is now available
      if (memItPage.session?.id === sessionId) {
        // Session is ready, send the message
        await memItPage.handleCreateUserMessage({
          content: message,
        });

        pendingMessage = null;
        return;
      }

      // Wait a bit before checking again
      await new Promise(resolve => setTimeout(resolve, INTERVAL_MS));
      attempts++;
    } while (attempts < maxAttempts);

    // If we timed out, log an error but still clear the pending message
    memItPage.setIsPendingNewChat(false);
    pendingMessage = null;
    logger.error({
      message: "[sendPendingChatMessage] Session not ready after timeout",
      info: {
        sessionId,
        message,
      },
    });
  } catch (error) {
    memItPage.setIsPendingNewChat(false);
    pendingMessage = null;
    logger.error({
      message: "[sendPendingChatMessage] Failed to send pending message",
      info: {
        error: objectModule.safeErrorAsJson(error as Error),
        message,
      },
    });
  }
};
